import { useParams, useLocation } from "wouter";
import { Shell } from "@/components/layout/Shell";
import { useAuth } from "@/hooks/use-auth";
import { useLaudo, useUsuarios, useLaudoPdfStatus } from "@/hooks/use-data";
import { EditarDestinacao } from "@/components/laudos/EditarDestinacao";
import { PreencherRespostas } from "@/components/laudos/PreencherRespostas";
import { AssinarlLaudo } from "@/components/laudos/AssinarlLaudo";
import { FinalizarLaudo } from "@/components/laudos/FinalizarLaudo";
import { BaixaLaudo } from "@/components/laudos/BaixaLaudo";
import { SelecionarTecnicoVistoria } from "@/components/laudos/SelecionarTecnicoVistoria";
import { AguardandoCreditoBadge } from "@/components/laudos/AguardandoCreditoBadge";
import { EnvioTecnicoVistoria } from "@/components/laudos/EnvioTecnicoVistoria";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogClose } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  AlertCircle,
  FileText,
  Calendar,
  User,
  MapPin,
  Wrench,
  ArrowLeft,
  Trash2,
  Loader2,
  Lock,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { tituloLaudo } from "@/lib/laudo-identificador";
import {
  VEICULO_TIPO_LABELS,
  VEICULO_TIPO_COLORS,
  LAUDO_STATUS_LABELS,
  LAUDO_STATUS_COLORS,
  DESTINACAO_LABELS,
  DESTINACAO_COLORS,
} from "@/lib/constants";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { api } from "@shared/routes";
import { AdicionarCartela } from "@/components/laudos/AdicionarCartela";
import { ConfirmarCartela } from "@/components/laudos/ConfirmarCartela";
import { FotoObservacaoPeca } from "@/components/laudos/FotoObservacaoPeca";
import { MiniaturasVeiculo } from "@/components/veiculos/FotosVeiculo";
import { bloqueioRespostasLaudo, podeEditarMidiaLaudo } from "@shared/laudo-midia";
import { bloqueioDestinacao } from "@shared/laudo-checklist";

export default function LaudoDetalhes() {
  const { id } = useParams();
  const [, navigate] = useLocation();
  const { profile, session } = useAuth();
  const { data: laudo, isLoading, error, refetch } = useLaudo(id);
  const { data: pdfStatus, isLoading: pdfStatusLoading } = useLaudoPdfStatus(laudo?.status === "finalizado" ? id : undefined);
  const { toast } = useToast();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSavingDestinacoes, setIsSavingDestinacoes] = useState(false);
  const [isSigning, setIsSigning] = useState(false);
  const [imagemAberta, setImagemAberta] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const isOwner = profile?.roles?.includes("owner") ?? false;

  // Admin da vistoriadora pode trocar o técnico mesmo quando já vinculado
  const isAdminVistoriadora =
    (profile?.roles?.includes("admin") ?? false) &&
    !!laudo &&
    profile?.empresa?.id === laudo.vistoriadoraId;

  const isAdminOrRt =
    (profile?.roles?.includes("admin") || profile?.roles?.includes("rt")) &&
    !!laudo &&
    profile?.empresa?.id === laudo.vistoriadoraId;

  const { data: usuarios } = useUsuarios(
    laudo?.vistoriadoraId,
    isAdminVistoriadora,
  );
  const tecnicosDisponiveis =
    usuarios?.filter((u) => u.roles?.includes("tv")) ?? [];

  const [editandoTecnico, setEditandoTecnico] = useState(false);

  const trocarTecnicoMutation = useMutation({
    mutationFn: async (tecnico: { id: string }) => {
      if (!laudo) throw new Error("Laudo não carregado");
      const res = await fetch(`/api/laudos/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ tecnicoVistoriaId: tecnico.id }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Erro ao atualizar técnico");
      }
      return res.json();
    },
    onSuccess: () => {
      refetch();
      toast({ title: "Técnico de vistoria atualizado com sucesso." });
    },
    onError: (err: Error) => {
      toast({
        title: "Erro",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/laudos/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (!res.ok) {
        const text = await res.text();
        let msg = "Erro ao excluir";
        try {
          msg = JSON.parse(text).message || msg;
        } catch {}
        throw new Error(msg);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.laudos.list.path] });
      queryClient.invalidateQueries({ queryKey: [api.laudos.stats.path] });
      toast({ title: "Laudo excluído com sucesso" });
      navigate("/laudos");
    },
    onError: (err: Error) => {
      toast({
        title: "Erro ao excluir laudo",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  // Verificar permissões de acesso
  const canAccess = () => {
    if (!profile || !laudo) return false;

    // Proprietário do sistema tem sempre acesso
    if (profile.roles?.includes("owner")) return true;

    // Vistoriadora ou Parceiro de Negócios que criou o laudo
    const isVistoriadora = profile.empresa?.id === laudo.vistoriadoraId;
    const isParceiro = profile.empresa?.id === laudo.parceiroId;

    return isVistoriadora || isParceiro;
  };

  // Verificar se usuário é responsável técnico ou admin
  const isResponsavelTecnico = () => {
    if (!profile || !laudo) return false;
    return (
      profile.roles?.includes("rt") && profile.id === laudo.responsavelTecnicoId
    );
  };

  // Verificar se usuário é técnico de vistoria
  const isTecnicoVistoria = () => {
    if (!profile || !laudo) return false;
    return profile.id === laudo.tecnicoVistoriaId;
  };

  // Pode editar ou executar ações? (nunca quando finalizado)
  const canEdit =
    laudo?.status !== "finalizado" && (isResponsavelTecnico() || canAccess());

  // Checklist do Parceiro e destinação: a mesma regra do servidor (shared/laudo-checklist).
  const bloqueioChecklist =
    profile && laudo
      ? bloqueioDestinacao(
          { empresaId: profile.empresa?.id, roles: profile.roles, userId: profile.id },
          laudo,
        )
      : "sem_permissao";
  const podeEditarDestinacao = bloqueioChecklist === null;
  const textoBloqueioChecklist =
    bloqueioChecklist === "em_vistoria"
      ? "O laudo já está em vistoria. O checklist do Parceiro está disponível somente para consulta."
      : bloqueioChecklist === "assinado" || bloqueioChecklist === "finalizado"
        ? "Este laudo está encerrado para edição."
        : null;

  // Pode editar cartela? (Parceiro de Negócio OU Admin/RT da vistoriadora)
  // Se cartela estiver confirmada, apenas OWNER pode editar
  const canEditCartela = () => {
    if (!profile || !laudo || laudo.status === "finalizado") return false;

    // Se cartela está confirmada, apenas OWNER pode editar
    if (laudo.cartelaConfirmada && !isOwner) {
      return false;
    }

    const isParceiro = profile.empresa?.id === laudo.parceiroId;
    const isAdminOrRtVistoriadora =
      profile.empresa?.id === laudo.vistoriadoraId &&
      (profile.roles?.includes("admin") || profile.roles?.includes("rt"));

    return isParceiro || isAdminOrRtVistoriadora || isOwner;
  };

  // Pode confirmar cartela? (Parceiro de Negócio OU Admin/RT da vistoriadora)
  const canConfirmCartela = () => {
    if (!profile || !laudo || !laudo.cartela || laudo.cartelaConfirmada) {
      return false;
    }

    const isParceiro = profile.empresa?.id === laudo.parceiroId;
    const isAdminOrRtVistoriadora =
      profile.empresa?.id === laudo.vistoriadoraId &&
      (profile.roles?.includes("admin") || profile.roles?.includes("rt"));

    return isParceiro || isAdminOrRtVistoriadora;
  };

  // Funções de ação
  // Devolve false quando alguma peça foi recusada: o diálogo de destinação fica aberto.
  const handleDestinacoesSave = async (
    updates: { id: string; destinacao: string }[],
  ): Promise<boolean> => {
    setIsSavingDestinacoes(true);
    try {
      const respostas = await Promise.all(
        updates.map((u) =>
          fetch(`/api/laudo-pecas/${u.id}`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${session?.access_token}`,
            },
            body: JSON.stringify({ destinacao: u.destinacao }),
          }).catch(() => null), // sem rede
        ),
      );
      refetch(); // mesmo com recusa, parte das peças pode ter salvo
      const recusada = respostas.findIndex((r) => !r?.ok);
      if (recusada < 0) return true;
      const corpo = (await respostas[recusada]?.json().catch(() => ({}))) ?? {};
      toast({
        variant: "destructive",
        title: "Erro ao salvar destinações",
        description: corpo.message ?? "Tente novamente.",
      });
      return false;
    } finally {
      setIsSavingDestinacoes(false);
    }
  };

  const handleAssinatura = async () => {
    setIsSigning(true);
    try {
      const response = await fetch(`/api/laudos/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          dataAssinatura: new Date(),
          status: "assinado",
        }),
      });

      if (response.ok) {
        await refetch();
        toast({ title: "Laudo assinado com sucesso!" });
      } else {
        const data = await response.json().catch(() => ({}));
        toast({
          variant: "destructive",
          title: "Erro ao assinar laudo",
          description: data.message || `Erro ${response.status}`,
        });
      }
    } catch (error) {
      console.error("Erro ao assinar:", error);
      toast({ variant: "destructive", title: "Erro ao assinar laudo" });
    } finally {
      setIsSigning(false);
    }
  };

  const handleFinalizacao = async (pdfUrl: string) => {
    try {
      const response = await fetch(`/api/laudos/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ status: "finalizado", pdfUrl }),
      });

      if (response.ok) {
        refetch();
        toast({ title: "Laudo finalizado e PDF gerado com sucesso!" });
      } else {
        const data = await response.json().catch(() => ({}));
        toast({
          variant: "destructive",
          title: "Erro ao finalizar laudo",
          description: data.message || `Erro ${response.status}`,
        });
      }
    } catch (error) {
      console.error("Erro ao finalizar:", error);
      toast({ variant: "destructive", title: "Erro ao finalizar laudo" });
    }
  };

  if (error) {
    return (
      <Shell>
        <div className="mb-8">
          <h1 className="text-3xl font-bold font-display text-slate-900">
            Detalles do Laudo
          </h1>
        </div>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Laudo não encontrado ou acesso negado
          </AlertDescription>
        </Alert>
      </Shell>
    );
  }

  if (isLoading || !laudo) {
    return (
      <Shell>
        <div className="mb-8">
          <Skeleton className="h-10 w-48 mb-2" />
          <Skeleton className="h-5 w-96 mb-4" />
        </div>
        <Skeleton className="h-64 rounded-xl mb-4" />
        <Skeleton className="h-64 rounded-xl" />
      </Shell>
    );
  }

  if (!canAccess()) {
    return (
      <Shell>
        <div className="mb-8">
          <h1 className="text-3xl font-bold font-display text-slate-900">
            Detalhes do Laudo
          </h1>
        </div>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Você não tem permissão para acessar este laudo
          </AlertDescription>
        </Alert>
      </Shell>
    );
  }

  return (
    <Shell>
      {/* Cabeçalho */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-8 gap-4">
        <div className="flex items-start gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/laudos")}
            className="mt-1"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold font-display text-slate-900 mb-2">
              {tituloLaudo(laudo)}
            </h1>
            <p className="text-muted-foreground">
              {laudo.veiculo?.marca} {laudo.veiculo?.modelo} -{" "}
              {laudo.veiculo?.placa}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Badge
            variant="secondary"
            className={cn(
              "text-base px-3 py-1",
              LAUDO_STATUS_COLORS[laudo.status],
            )}
          >
            {LAUDO_STATUS_LABELS[laudo.status] || laudo.status}
          </Badge>
          <AguardandoCreditoBadge laudo={laudo} />
        </div>
      </div>

      {/* Alertas */}
      {!canEdit && (
        <Alert className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Este laudo está em modo somente leitura. Entre em contato com o
            responsável técnico para fazer alterações.
          </AlertDescription>
        </Alert>
      )}

      {/* Informações Gerais */}
      <div className="rounded-xl border bg-card border-card-border text-card-foreground shadow-sm mb-6">
        <div className="flex flex-col space-y-1.5 p-6">
          <h2 className="text-xl font-bold text-slate-900">
            Informações Gerais
          </h2>
        </div>
        <div className="p-6 pt-0 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Vistoriadora */}
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">
                Empresa Vistoriadora
              </p>
              <p className="text-sm font-semibold text-slate-900">
                {laudo.vistoriadora?.nomeFantasia ||
                  laudo.vistoriadora?.razaoSocial}
              </p>
            </div>

            {/* Parceiro */}
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">
                Parceiro de Negócios
              </p>
              <p className="text-sm font-semibold text-slate-900">
                {laudo.parceiro?.nomeFantasia || laudo.parceiro?.razaoSocial}
              </p>
            </div>

            {laudo.parceiro?.art && laudo.parceiro?.vencimentoArt && (
              <div>
                <p className="text-sm font-medium text-slate-500 mb-1">
                  <User className="w-4 h-4 inline mr-1" />
                  ART
                </p>
                <p className="text-sm font-semibold text-slate-900">
                  {laudo.parceiro.art}
                </p>

                <p className="text-xs text-slate-500">
                  VENCIMENTO: &nbsp;
                  {new Date(laudo.parceiro.vencimentoArt!).toLocaleDateString(
                    "pt-BR",
                  )}
                </p>
              </div>
            )}

            {/* Cartela */}
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">Cartela</p>
              {laudo.cartela ? (
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-slate-900 font-mono">
                    {laudo.cartela}
                  </p>
                  {laudo.cartelaConfirmada && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                      ✓ Confirmada
                    </span>
                  )}
                </div>
              ) : (
                <p className="text-sm text-slate-400 italic">Sem cartela</p>
              )}
            </div>

            {/* Data de Criação */}
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Data de Criação
              </p>
              <p className="text-sm font-semibold text-slate-900">
                {new Date(laudo.criadoEm).toLocaleDateString("pt-BR")}
              </p>
            </div>

            {/* Data de Vistoria */}
            {laudo.dataVistoria && (
              <div>
                <p className="text-sm font-medium text-slate-500 mb-1">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Data de Vistoria
                </p>
                <p className="text-sm font-semibold text-slate-900">
                  {new Date(laudo.dataVistoria).toLocaleDateString("pt-BR")}
                </p>
              </div>
            )}
          </div>

          {/* Botão para Adicionar/Editar Cartela - oculto quando confirmada */}
          {!laudo.cartelaConfirmada &&
            (canEditCartela() || canConfirmCartela()) && (
              <div className="border-t pt-4 flex gap-2 flex-wrap">
                <AdicionarCartela
                  cartelaAtual={laudo.cartela}
                  laudoId={laudo.id}
                  onSuccess={refetch}
                  cartelaConfirmada={laudo.cartelaConfirmada}
                  vistoriadoraId={laudo.vistoriadoraId}
                  parceiroId={laudo.parceiroId}
                  tipoVeiculo={laudo.veiculo?.tipo}
                />
                {canConfirmCartela() && (
                  <ConfirmarCartela
                    cartela={laudo.cartela!}
                    laudoId={laudo.id}
                    onSuccess={refetch}
                  />
                )}
              </div>
            )}

          {/* Usuários Envolvidos */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t pt-4">
            {laudo.tecnicoVistoria && (
              <div>
                <p className="text-sm font-medium text-slate-500 mb-1">
                  <User className="w-4 h-4 inline mr-1" />
                  Técnico de Vistoria
                </p>
                <p className="text-sm font-semibold text-slate-900">
                  {laudo.tecnicoVistoria.nome}
                </p>
                {isAdminVistoriadora && laudo.status !== "finalizado" && (
                  <div className="mt-2">
                    <SelecionarTecnicoVistoria
                      tecnicosDisponiveis={tecnicosDisponiveis}
                      tecnicoAtual={laudo.tecnicoVistoria}
                      laudoNaoVisitoriado={true}
                      exigeFotosVeiculo={laudo.moduloDetranVeiculoFoto}
                      veiculoId={laudo.veiculoId}
                      isLoading={editandoTecnico}
                      onSelect={async (tecnico) => {
                        setEditandoTecnico(true);
                        try {
                          await trocarTecnicoMutation.mutateAsync(tecnico);
                        } finally {
                          setEditandoTecnico(false);
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            )}

            {laudo.responsavelTecnico && (
              <div>
                <p className="text-sm font-medium text-slate-500 mb-1">
                  <User className="w-4 h-4 inline mr-1" />
                  Responsável Técnico
                </p>
                <p className="text-sm font-semibold text-slate-900">
                  {laudo.responsavelTecnico.nome}
                </p>
                {laudo.responsavelTecnico.crea && (
                  <p className="text-xs text-slate-500">
                    CREA: {laudo.responsavelTecnico.crea}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Informações do Veículo */}
      {laudo.veiculo && (
        <Card className="mb-6">
          <CardHeader>
            <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Informações do Veículo
            </h2>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <p className="text-sm font-medium text-slate-500 mb-1">
                  Marca/Modelo
                </p>
                <p className="text-sm font-semibold text-slate-900">
                  {laudo.veiculo.marca} {laudo.veiculo.modelo}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500 mb-1">Placa</p>
                <p className="text-sm font-semibold text-slate-900 font-mono">
                  {laudo.veiculo.placa}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500 mb-1">Tipo</p>
                <p className="text-sm font-semibold text-slate-900">
                  <Badge
                    className={
                      VEICULO_TIPO_COLORS[laudo.veiculo.tipo] ??
                      "bg-slate-100 text-slate-700"
                    }
                  >
                    {VEICULO_TIPO_LABELS[laudo.veiculo.tipo] ||
                      laudo.veiculo.tipo}
                  </Badge>
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500 mb-1">Ano</p>
                <p className="text-sm font-semibold text-slate-900">
                  {laudo.veiculo.anoModelo || laudo.veiculo.anoFabricacao}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500 mb-1">Cor</p>
                <p className="text-sm font-semibold text-slate-900">
                  {laudo.veiculo.cor}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500 mb-1">
                  Chassi
                </p>
                <p className="text-sm font-semibold text-slate-900 font-mono text-xs">
                  {laudo.veiculo.chassi}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Envio direto ao Técnico de Vistoria (Parceiro de vistoriadora com TV único) */}
      <EnvioTecnicoVistoria laudo={laudo} onEnviado={refetch} />

      {/* Peças do Laudo */}
      {laudo.laudoPecas && laudo.laudoPecas.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <Wrench className="w-5 h-5" />
                Peças Avaliadas
              </h2>
              <Badge variant="outline" className="ml-auto">
                {laudo.laudoPecas.length} peça(s)
              </Badge>
            </div>

            {/* Resumo de Destinações (apenas para laudos finalizados) */}
            {laudo.status === "finalizado" &&
              (() => {
                const counts = laudo.laudoPecas!.reduce<Record<string, number>>(
                  (acc, p) => {
                    if (p.destinacao)
                      acc[p.destinacao] = (acc[p.destinacao] || 0) + 1;
                    return acc;
                  },
                  {},
                );
                if (Object.keys(counts).length === 0) return null;
                return (
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-xs font-medium text-slate-500 mb-2">
                      Resumo de Destinações
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(counts).map(([dest, count]) => (
                        <Badge
                          key={dest}
                          variant="secondary"
                          className={cn("text-xs", DESTINACAO_COLORS[dest])}
                        >
                          {DESTINACAO_LABELS[dest]}: {count}
                        </Badge>
                      ))}
                    </div>
                  </div>
                );
              })()}
          </CardHeader>
          <CardContent>
            {textoBloqueioChecklist && (
              <p className="mb-4 flex items-start gap-2 rounded-lg border bg-slate-50 p-3 text-sm text-slate-600">
                <Lock className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden />
                {textoBloqueioChecklist}
              </p>
            )}
            {/* Fotos existentes são consultáveis também nos laudos antigos.
                A flag congelada continua controlando as exigências do fluxo. */}
            <div className="mb-4 empty:hidden">
              <MiniaturasVeiculo fotos={laudo.veiculo?.fotos ?? []} onAmpliar={setImagemAberta} />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {laudo.laudoPecas?.map((peca) => (
                <Card key={peca.id} className="bg-slate-50">
                  <CardContent className="p-4">
                    <div className="space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-900">
                            {peca.nome}
                          </p>
                          <p className="text-xs text-slate-500">
                            {peca.uf} -{" "}
                            {VEICULO_TIPO_LABELS[peca.tipoVeiculo] ||
                              peca.tipoVeiculo}
                          </p>
                          {peca.destinacao && (
                            <Badge
                              variant="secondary"
                              className={cn(
                                "text-xs mt-1",
                                DESTINACAO_COLORS[peca.destinacao],
                              )}
                            >
                              {DESTINACAO_LABELS[peca.destinacao]}
                            </Badge>
                          )}
                        </div>
                        {laudo.cartela ? (
                          peca.etiqueta && (
                            <Badge
                              variant="outline"
                              className="text-xs shrink-0"
                            >
                              {peca.etiqueta}
                            </Badge>
                          )
                        ) : (
                          <Badge
                            variant="secondary"
                            className="text-xs shrink-0"
                          >
                            {peca.sequencia}
                          </Badge>
                        )}
                      </div>

                      <FotoObservacaoPeca
                        peca={peca}
                        podeEditar={!!profile && podeEditarMidiaLaudo({ ...profile, empresaId: profile.empresa?.id }, laudo)}
                        ocultarObservacao={profile?.roles?.includes("parceiro_negocios") === true}
                        onSaved={async () => {
                          await refetch();
                          await queryClient.invalidateQueries({ queryKey: [api.laudos.list.path] });
                        }}
                        onAmpliar={setImagemAberta}
                      />

                      {/* Respostas da Peça */}
                      {peca.respostas && peca.respostas.length > 0 && (
                        <details className="mt-3 border-t pt-3 group">
                          <summary className="cursor-pointer text-xs font-medium text-slate-600 marker:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm">
                            Checklist da peça ({peca.respostas.filter((r) => r.resposta === true).length} de {peca.respostas.length} aplicadas)
                          </summary>
                          <div className="mt-2 space-y-2.5">
                            {peca.respostas.map((r, idx) => (
                              <div key={r.id} className="text-xs bg-slate-100 p-2 rounded">
                                <div className="flex items-start gap-2">
                                  <span className="font-medium text-slate-400 shrink-0">{idx + 1}.</span>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-slate-700 font-medium break-words">{r.pergunta?.texto}</p>
                                    <p className={`mt-1 font-semibold ${r.resposta === true ? "text-green-700" : r.resposta === false ? "text-red-700" : "text-slate-500 italic"}`}>
                                      {r.resposta === true ? "✓ Sim" : r.resposta === false ? "✗ Não" : "—"}
                                    </p>
                                    {r.observacoes?.trim() && (
                                      <p className="mt-1.5 text-slate-600 italic"><span className="font-medium">Obs:</span>{" "}{r.observacoes}</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}

                      {/* Botão Responder (visível se há perguntas ou se quem edita pode sugerir a destinação) */}
                      {canEdit &&
                        profile &&
                        bloqueioRespostasLaudo({ empresaId: profile.empresa?.id, roles: profile.roles }, laudo) === null &&
                        ((peca.respostas?.length ?? 0) > 0 || podeEditarDestinacao) && (
                          <div className="pt-2">
                            <PreencherRespostas
                              laudoPecaId={peca.id}
                              nomePeca={peca.nome}
                              respostas={peca.respostas ?? []}
                              destinacao={peca.destinacao}
                              fotoUrl={peca.fotoUrl}
                              moduloDetran={laudo.moduloDetranVeiculoFoto}
                              podeEditarDestinacao={podeEditarDestinacao}
                              onSaved={refetch}
                            />
                          </div>
                        )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Assinatura e PDF */}
      {(laudo.assinaturaUrl || laudo.pdfUrl) && (
        <Card className="mb-6">
          <CardHeader>
            <h2 className="text-xl font-bold text-slate-900">Documentos</h2>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {laudo.assinaturaUrl && isAdminOrRt && (
              <a
                href={laudo.assinaturaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
              >
                Ver Assinatura
              </a>
            )}
            {laudo.pdfUrl && (
              pdfStatusLoading ? (
                <Skeleton className="h-10 w-40" />
              ) : pdfStatus?.bloqueado ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
                  <Lock className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-amber-800 text-sm">PDF indisponível</p>
                    <p className="text-amber-700 text-xs mt-1 leading-relaxed">
                      {pdfStatus.motivo ?? "Este laudo possui uma fatura pendente de pagamento. O download será liberado automaticamente após a confirmação do pagamento pela vistoriadora."}
                    </p>
                  </div>
                </div>
              ) : (
                <a
                  // ponytail: o PDF reemitido mantém o mesmo caminho no Storage (cache de 1 h); a versão força o arquivo novo
                  href={`${laudo.pdfUrl}${laudo.pdfUrl.includes("?") ? "&" : "?"}v=${new Date(laudo.atualizadoEm ?? 0).getTime()}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => { void apiRequest("POST", "/api/auditoria/pdf-baixado", { laudoId: laudo.id }).catch(() => {}); }}
                  className="inline-flex items-center justify-center px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                >
                  Baixar PDF
                </a>
              )
            )}
          </CardContent>
        </Card>
      )}

      {/* Ações */}
      <BaixaLaudo laudo={laudo} onSaved={async () => {
        await queryClient.invalidateQueries({ queryKey: [api.laudos.list.path] });
        await queryClient.invalidateQueries({ queryKey: [api.laudos.stats.path] });
        await refetch();
      }} />
      {(canEdit || isOwner) && (
        <Card className="bg-slate-50 border-slate-200">
          <CardHeader>
            <h2 className="text-xl font-bold text-slate-900">Ações</h2>
            <CardDescription>
              {isResponsavelTecnico()
                ? "Como responsável técnico, você pode editar e executar ações do laudo"
                : "Opções de edição e ações"}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            {isResponsavelTecnico() && !isEditMode && (
              <>
                {laudo.laudoPecas && laudo.laudoPecas.length > 0 && (
                  <EditarDestinacao
                    laudoPecas={laudo.laudoPecas}
                    moduloDetran={laudo.moduloDetranVeiculoFoto}
                    onSave={handleDestinacoesSave}
                    isLoading={isSavingDestinacoes}
                  />
                )}
                {!laudo.assinaturaUrl && (
                  <AssinarlLaudo
                    laudo={laudo}
                    onSign={handleAssinatura}
                    isLoading={isSigning}
                  />
                )}
                <FinalizarLaudo
                  laudo={laudo}
                  onFinalize={(pdfUrl) => handleFinalizacao(pdfUrl)}
                />
              </>
            )}
            {isOwner && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Excluir Laudo
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Confirmação de Exclusão */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir este laudo?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação é irreversível. O laudo, todas as peças, fotos e PDF
              serão permanentemente excluídos. Se estiver finalizado, será
              removido do lote correspondente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteMutation.mutate()}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal de Imagem */}
      <Dialog open={!!imagemAberta} onOpenChange={() => setImagemAberta(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0 bg-black border-0">
          <div className="flex items-center justify-center w-full h-full">
            <img
              src={imagemAberta || ""}
              alt="Imagem ampliada"
              className="max-w-full max-h-[85vh] object-contain"
            />
          </div>
          <DialogClose className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-0 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-0 disabled:pointer-events-none data-[state=open]:bg-transparent data-[state=open]:text-white">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </DialogClose>
        </DialogContent>
      </Dialog>

    </Shell>
  );
}
